import React, { useEffect, useMemo } from 'react';
import { useCompanyData } from '../contexts/DataContext';
import SectionLayout from '../../shared/layout/SectionLayout';
import firebase, { db, processCustomFormData } from 'src/config/firebase';
import { Box, useMediaQuery, Button } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { AlertMessage } from 'src/screens/Partners/Service/components';
import { withStyles } from '@mui/styles';
import { TextField, CircularProgress, Backdrop, FormControl, InputLabel, MenuItem, Typography, Select, FormHelperText, FormControlLabel, FormGroup,FormLabel,Checkbox, Divider, adaptV4Theme, createTheme, ThemeProvider, Skeleton  } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import rtlPlugin from 'stylis-plugin-rtl';
import { position, prefixer } from 'stylis';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import useRTL from 'src/hooks/useRTL';
import { useDropzone } from 'react-dropzone';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { SectionTitle as T1SectionTitle, SectionSubtitle as T1SectionSubTitle } from '../../Template1/components/SectionTitle';
import { SectionTitle as T2SectionTitle, SectionSubtitle as T2SectionSubTitle } from '../../Template2/components/SectionTitle';
import { SectionTitle as T3SectionTitle, SectionSubtitle as T3SectionSubTitle } from '../../Template3/components/SectionTitle';
import { SectionTitle as T4SectionTitle, SectionSubtitle as T4SectionSubTitle } from '../../Template4/components/SectionTitle';
import { textClasses } from '../config';
import '../../shared/styles.css';

const ColorCircularProgress = withStyles({
    root: {
      color: '#0F467A',
    },
})(CircularProgress);

const Theme1 = createTheme(adaptV4Theme({
  palette: {
    text: { primary: '#011615', secondary: '#0BDED6' },
    primary: { main: '#0BDED6' },
    secondary: { main: '#01161599' },
  },
  typography: {
    allVariants: {
      fontFamily: 'inherit',
    },
    h5: {
      fontWeight: 700,
    },
  },
  overrides: {
    img: {
      width: '100%',
    },
    MuiAppBar: { root: { boxShadow: 'none' } },

    MuiButton: {
      allVariants: {
        fontFamily: 'inherit',
      },
      root: {
        fontWeight: 600,
        textTransform: 'capitalize',
        boxShadow: 'none',
      },
      containedPrimary: {
        color: 'white',
        boxShadow: 'none',
        '&:hover': {
          boxShadow: 'none',
        },
      },
    },
    MuiCard: {
      root: {
        borderRadius: '4px',
        boxShadow: '0 0 5px rgb(209, 209, 209)',
      },
    },
  },
}));

const Theme2 = createTheme(adaptV4Theme({
  palette: {
    text: { primary: '#011615', secondary: '#17C799' },
    primary: { main: '#17C799' },
    secondary: { main: '#01161599' },
    background: { default: 'red' },
  },
  typography: {
    allVariants: {
      fontFamily: 'inherit',
    },
    h5: {
      fontWeight: 700,
      fontFamily: 'inherit',
      fontSize: 'inherit',
    },
    h6: {
      fontWeight: 500,
    },
    body1: {
      fontSize: 'inherit',
    },
  },
  overrides: {
    MuiAppBar: { root: { boxShadow: 'none' } },

    MuiButton: {
      allVariants: {
        fontFamily: 'inherit',
      },
      root: {
        fontWeight: 500,
        textTransform: 'capitalize',
        boxShadow: 'none',
      },
      containedPrimary: {
        color: 'white',
        boxShadow: 'none',
        '&:hover': {
          boxShadow: 'none',
        },
      },
    },
    MuiCard: {
      root: {
        borderRadius: '8px',
        boxShadow: '0 0 30px rgb(209, 209, 209)',
      },
    },
  },
}));

const Theme3 = createTheme(adaptV4Theme({
  palette: {
    text: { primary: '#110729', secondary: '#FAC400' },
    primary: { main: '#FAC400' },
    secondary: { main: '#01161599' },
  },
  typography: {
    allVariants: {
      fontFamily: 'inherit',
    },
    h5: {
      fontWeight: 700,
      fontSize: 'inherit',
    },
    h6: {
      fontWeight: 500,
    },
  },
  overrides: {
    MuiAppBar: { root: { boxShadow: 'none' } },

    MuiButton: {
      allVariants: {
        fontFamily: 'inherit',
      },
      root: {
        textTransform: 'capitalize',
        boxShadow: 'none',
        borderRadius: 0,
      },
      containedPrimary: {
        color: 'white',
        boxShadow: 'none',
        '&:hover': {
          boxShadow: 'none',
        },
      },
    },
    MuiCard: {
      root: {
        borderRadius: '8px',
        boxShadow: '0 0 30px rgb(209, 209, 209)',
      },
    },
  },
}));

const Theme4 = createTheme(adaptV4Theme({
  direction:'rtl',
  palette: {
    text: { primary: '#011615', secondary: '#C31B37' },
    primary: { main: '#C31B37' },
    secondary: { main: '#01161599' },
    background: { default: 'red' },
  },
  typography: {
    allVariants: {
      fontFamily: 'inherit',
    },
    h5: {
      fontWeight: 700,
    }
  },
  overrides: {
    MuiAppBar: { root: { boxShadow: 'none' } },

    MuiButton: {
      root: {
        // fontFamily: 'Nunito Sans',
        fontWeight: 600,
        textTransform: 'capitalize',
        boxShadow: 'none',
      },
      containedPrimary: {
        color: 'white',
        boxShadow: 'none',
        '&:hover': {
          boxShadow: 'none',
        },
      },
    },
    MuiCard: {
      root: {
        borderRadius: '4px',
        boxShadow: '0 0 5px rgb(209, 209, 209)',
      },
    },
  },
}));

const _RTLTheme = (outerTheme) =>
    createTheme({
        ...outerTheme, // 🔥 Preserve global theme settings
      direction:'rtl'
});

const _LTRTheme = (outerTheme) =>
    createTheme({
        ...outerTheme, // 🔥 Preserve global theme settings
      direction:'ltr'
});

const cacheRtl = createCache({
    key: 'muirtl',
    stylisPlugins: [prefixer, rtlPlugin],
});



const useStyles = makeStyles(() => ({
    thumb: {
        display: 'inline-flex',
        borderRadius: 2,
        border: '1px solid #eaeaea',
        marginBottom: 8,
        marginRight: 8,
        width: 100,
        height: 100,
        padding: 4,
        boxSizing: 'border-box'
    },
      
    thumbInner: {
        display: 'flex',
        minWidth: 0,
        overflow: 'hidden'
    },
      
    img: {
        display: 'block',
        width: 'auto',
        height: '100%'
    },

    temp3MyLabel: {
        color: '#0A0945 !important',
        opacity: '0.6',
        textTransform:'capitalize',
        transition: 'transform 0.2s ease-out, font-size 0.2s ease-out', // Smooth transition
        '&.Mui-focused': {
          fontWeight: 'bold',
          color: '#0A0945 !important',
        }
    },

    temp3MyLabelColor:{
        color: '#0A0945 !important',
        opacity: '0.6',
        textTransform:'capitalize',
        transition: 'transform 0.2s ease-out, font-size 0.2s ease-out', // Smooth transition
        '&.Mui-focused': {
            fontWeight: 'bold',
            color: '#0A0945 !important',
        }
    },

    dialogBackdrop: {
        zIndex: 111125,
        color: '#fff',
        position:'absolute',
        borderRadius:'10px',
        backgroundColor:'rgba(0,0,0,0.1)'
    },

    temp3CheckboxLabelColor:{
        color: '#0A0945 !important',
        opacity: '0.6',
        textTransform:'capitalize',
        transition: 'transform 0.2s ease-out, font-size 0.2s ease-out', // Smooth transition
        '&.Mui-focused': {
            fontWeight: 'bold',
            color: '#0A0945 !important',
        }
    },

    temp2MyLabelColor:{
        color: '#15476D',
        opacity: '0.6',
        transition: 'transform 0.2s ease-out, font-size 0.2s ease-out', // Smooth transition
        '&.Mui-focused': {
          fontWeight: 'bold',
          color: '#0A0945 !important',
        }
    },

    temp3InputCl:{
        padding:'12px 14px',
        '&.Mui-focused': {
          fontWeight: 'bold',
          color: '#0A0945',
        },
    },

    temp3FocusedCl:{
        borderColor:'#0A0945 !important',
        '&.MuiOutlinedInput-notchedOutline': {
          borderColor: '#0A0945 !important',
        },
    },

    // Select input styles
    temp3SelectInputCl: {
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: '#0A0945 !important',  // Custom border color
        },
       '& .MuiSelect-select': {  
            padding: '11.5px 14px'  // Adjust padding
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: '#0A0945 !important',  // Focused border color
        },
    },

    dateInput:{
        '& .MuiTextField-root': {
            width:'100%'
        },
    },

    selectedMenuItem: {
        backgroundColor: '#fff', // Change background color for selected item
        color: '#0A0945 !important',
        '&.Mui-selected': {
          backgroundColor: '#0A0945 !important', // Change background color for selected item
          color: '#ffffff !important', // Change text color for selected item
        },
        '&.Mui-selected:hover': {
          backgroundColor: '#0A0945 !important', // Ensure the color remains when hovered
        },
    },

    temp2MyLabel: {
        color: '#15476D',
        opacity: '0.6',
        transition: 'transform 0.2s ease-out, font-size 0.2s ease-out', // Smooth transition
        '&.Mui-focused': {
          fontWeight: 'bold',
          color: '#0A0945 !important',
        },
      },

      phoneInput:{
        '&.PhoneInputInput': {
            border:'1px solid red !important'
        }
      },


      linkCl:{
        color:'#007bff',
        '&:hover':{
            textDecoration:'underline',
            color:'#0056b3'
        }
      }
}));

// template 1
const Temp1Input = ({fieldLabel, fieldType, id, fieldPlaceHolder, textAreaRows, value, onChange, required, error }) => {
    return(
        <TextField
            label={`${fieldLabel}`}
            type={fieldType}
            id={id}
            value={value}
            onChange={(e) => onChange(id, e.target.value,fieldLabel,required)}
            fullWidth
            variant="outlined"
            {...(fieldType === 'textArea' ? {
                multiline:true,
                rows: textAreaRows || 3
            } : {})}
            style={{textTransform:'capitalize'}}
            required={required}
            placeholder={fieldPlaceHolder}
            error={error} // Highlights the input field in red if there's an error
            helperText={error ? `${fieldLabel} is required` : ""}
        />
    )
}
const Temp1SelctInput = ({fieldLabel, id, fieldOptions, t, value, onChange, required, error }) => {
    const classes = useStyles();
    return(
        <FormControl fullWidth variant="outlined">
            <InputLabel id={`${id}-label`}>{`${fieldLabel}${required ? '*' : ''}`}</InputLabel>
            <Select
                label={`${fieldLabel}${required ? '*' : ''}`}
                id={id}
                value={value}
                onChange={(e) => onChange(id, e.target.value,fieldLabel,required)}
                labelId="simple-select-label"
                style={{textTransform:'capitalize'}}
                variant="outlined"
            >
                <MenuItem value='' className={classes.selectedMenuItem}>{t && t('booking.booking-header.select')}</MenuItem>
                {fieldOptions?.map((option, index) => (<MenuItem key={index+1} value={option} className={classes.selectedMenuItem}>{option}</MenuItem>))}
            </Select>
            {error && <FormHelperText style={{color:'#d32f2f'}}>{fieldLabel} is required</FormHelperText>}
        </FormControl>
    )
}

// template 2
const Temp2Input = ({fieldLabel, fieldType, id, fieldPlaceHolder, textAreaRows, value, onChange, required, error }) => {
    const classes = useStyles();

    return(
        <TextField
            label={`${fieldLabel}`}
            type={fieldType}
            id={id}
            value={value}
            onChange={(e) => onChange(id, e.target.value,fieldLabel,required)}
            fullWidth
            variant="outlined"
            {...(fieldType === 'textArea' ? {
                multiline:true,
                rows: textAreaRows || 3
            } : {})}
            style={{textTransform:'capitalize'}}
            placeholder={fieldPlaceHolder}
            InputLabelProps={{ className: classes.temp2MyLabel }}
            required={required}
            error={error} // Highlights the input field in red if there's an error
            helperText={error ? `${fieldLabel} is required` : ""}
        />
    )
}
const Temp2SelctInput = ({fieldLabel, id, fieldOptions, t, value, onChange, required, error }) => {
    const classes = useStyles();
    return(
        <FormControl fullWidth variant="outlined">
            <InputLabel id={`${id}-label`} className={classes.temp2MyLabel}>{`${fieldLabel}${required ? '*' : ''}`}</InputLabel>
            <Select
                label={`${fieldLabel}${required ? '*' : ''}`}
                id={id}
                value={value}
                onChange={(e) => onChange(id, e.target.value,fieldLabel,required)}
                labelId="simple-select-label"
                style={{textTransform:'capitalize'}}
                variant="outlined"
            >
                <MenuItem value='' className={classes.selectedMenuItem}>{t && t('booking.booking-header.select')}</MenuItem>
                {fieldOptions?.map((option, index) => (<MenuItem key={index+1} value={option} className={classes.selectedMenuItem}>{option}</MenuItem>))}
            </Select>
            {error && <FormHelperText style={{color:'#d32f2f'}}>{fieldLabel} is required</FormHelperText>}
        </FormControl>
    )
}

// template 3
const Temp3Input = ({fieldLabel, fieldType, id, textAreaRows, fieldPlaceHolder, value, onChange, required, error }) => {
    const classes = useStyles();

    return(
        <TextField
            label={`${fieldLabel}`}
            type={fieldType}
            id={id}
            value={value}
            onChange={(e) => onChange(id, e.target.value,fieldLabel,required,required)}
            fullWidth
            variant="outlined"
            {...(fieldType === 'textArea' ? {
                multiline:true,
                rows: textAreaRows || 3
            } : {})}
            style={{textTransform:'capitalize'}}
            placeholder={fieldPlaceHolder}
            InputLabelProps={{ className: classes.temp3MyLabel }}
            InputProps={{classes:{input:classes.temp3InputCl,notchedOutline:classes.temp3FocusedCl}}}
            required={required}
            error={error} // Highlights the input field in red if there's an error
            helperText={error ? `${fieldLabel} is required` : ""}
        />
    )
}
const Temp3SelctInput = ({fieldLabel, id, fieldOptions, t, value, onChange, error, required }) => {
    const classes = useStyles();
    return(
        <FormControl fullWidth variant="outlined">
            <InputLabel id={`${id}-label`} className={classes.temp3MyLabel}>{`${fieldLabel}${required ? '*' : ''}`}</InputLabel>
            <Select
                label={`${fieldLabel}${required ? '*' : ''}`}
                id={id}
                value={value}
                onChange={(e) => onChange(id, e.target.value,fieldLabel,required)}
                labelId="simple-select-label"
                style={{textTransform:'capitalize'}}
                className={classes.temp3SelectInputCl}
                variant="outlined"
                InputProps={{
                    className: {
                        input: classes.temp3InputCl,  // Apply text color styles
                    }
                }}
            >
                <MenuItem value='' className={classes.selectedMenuItem}>{t && t('booking.booking-header.select')}</MenuItem>
                {fieldOptions?.map((option, index) => (<MenuItem key={index+1} value={option} className={classes.selectedMenuItem}>{option}</MenuItem>))}
            </Select>
            {error && <FormHelperText style={{color:'#d32f2f'}}>{fieldLabel} is required</FormHelperText>}
        </FormControl>
    )
}

// template 4
const Temp4Input = ({fieldLabel, fieldType, id, textAreaRows, fieldPlaceHolder, value, onChange, required, error, isRTL }) => {
    return(
        <div dir={isRTL === 'ar' ? 'rtl' : 'ltr'}>
            <TextField
                label={`${fieldLabel}`}
                type={fieldType}
                id={id}
                value={value}
                onChange={(e) => onChange(id, e.target.value,fieldLabel,required)}
                fullWidth
                variant="outlined"
                style={{textTransform:'capitalize'}}
                {...(fieldType === 'textArea' ? {
                    multiline:true,
                    rows: textAreaRows || 3
                } : {})}
                required={required}
                placeholder={fieldPlaceHolder}
                error={error} // Highlights the input field in red if there's an error
                helperText={error ? `${fieldLabel} is required` : ""}
            />
        </div>
    )
}
const Temp4SelctInput = ({fieldLabel, id, fieldOptions, t, value, onChange, required, error }) => {
    const classes = useStyles();
    return(
        <FormControl fullWidth variant="outlined">
            <InputLabel id={`${id}-label`}>{`${fieldLabel}${required ? '*' : ''}`}</InputLabel>
            <Select
                label={`${fieldLabel}${required ? '*' : ''}`}
                id={id}
                value={value}
                onChange={(e) => onChange(id, e.target.value,fieldLabel,required)}
                labelId="simple-select-label"
                style={{textTransform:'capitalize'}}
                variant="outlined"
            >
                <MenuItem value='' className={classes.selectedMenuItem}>{t && t('booking.booking-header.select')}</MenuItem>
                {fieldOptions?.map((option, index) => (<MenuItem key={index+1} value={option} className={classes.selectedMenuItem}>{option}</MenuItem>))}
            </Select>
            {error && <FormHelperText style={{color:'#d32f2f'}}>{fieldLabel} is required</FormHelperText>}
        </FormControl>
    )
}

// phone input
const TempPhoneInput = ({fieldLabel, id, value, onChange, required, error, themeDesign, isRTL }) => {
    const [countryCode,setCountryCode] = React.useState('SA');
    return(
        <>
        <PhoneInput
            placeholder={fieldLabel}
            defaultCountry={countryCode}
            id={id}
            value={value}
            style={{textAlign:isRTL === 'ar' ? 'right' : 'left'}}
            onChange={value => onChange(id,value,fieldLabel,required)}
            className={`${isRTL === 'ar' ? 'phoneAlignRight' : 'phoneAlignLeft'} ${themeDesign === 1 ? 'theme1' : themeDesign === 2 ? 'theme2' : themeDesign === 3 ? 'theme3' : themeDesign === 4 ? 'theme4' : ''}`}
            onCountryChange={countryCode => setCountryCode(countryCode)}
            error={value ? (isValidPhoneNumber(value) ? undefined : `Invalid ${fieldLabel}`) : `${fieldLabel} required`}
        />
        {error && <FormHelperText style={{color:'#d32f2f'}}>{fieldLabel} is required</FormHelperText>}
        </>
    )
}
// checkbox
const CheckboxInput = ({fieldLabel, id, horizontal, fieldOptions, value, onChange, required, error, themeDesign }) => {
    const classes = useStyles();
    return(
        <>
        <FormControl component="fieldset" variant="outlined">
            <FormLabel component="legend" shrink={false} className={themeDesign === 2 ? classes.temp2MyLabelColor : themeDesign === 3 ? classes.temp3MyLabelColor : ''}>{`${fieldLabel}${required ? '*' : ''}`}</FormLabel>
            <FormGroup style={horizontal ? {display:'flex',flexDirection:'row',alignItems:'center'} : {}}>
                {fieldOptions?.map((v,i)=> {
                    return (
                    <FormControlLabel
                        className={themeDesign === 2 ? classes.temp2MyLabelColor : themeDesign === 3 ? classes.temp3CheckboxLabelColor : ''}
                        key={i+1}
                        control={
                            <Checkbox checked={v === value} 
                            sx={{
                                color: themeDesign === 2 ? '#17C799' : themeDesign === 3 ? '#0A0945' : '',
                                '&.Mui-checked': {
                                  color: themeDesign === 2 ? '#17C799' : themeDesign === 3 ? '#0A0945' : '',
                                },
                            }}
                            onChange={e=> onChange(id,v,fieldLabel,required)} id={id} />
                        }
                        label={v}
                    />
                )
                })}
            </FormGroup>
            {error && <FormHelperText style={{color:'#d32f2f'}}>{fieldLabel} is required</FormHelperText>}
        </FormControl>
        </>
    )
}

const TermsCheckbox = ({fieldLabel, id, termLink, fieldOptions, value, onChange, required, error, themeDesign, isRTL }) => {
    const classes = useStyles();
    return(
        <>
        <div style={{display:'flex',flexDirection:'row',alignItems:'center'}}>
            <Checkbox checked={value} 
                sx={{
                    color: themeDesign === 2 ? '#17C799' : themeDesign === 3 ? '#0A0945' : '',
                    '&.Mui-checked': {
                        color: themeDesign === 2 ? '#17C799' : themeDesign === 3 ? '#0A0945' : '',
                    },
                }}
                onChange={e=> onChange(id,!value,fieldLabel,required)} id={id} 
            />
            <a
                href={termLink}
                target="_blank"        
                rel="noopener noreferrer" 
                title={fieldLabel}
                className={classes.linkCl}
                style={isRTL === 'ar' ? {paddingRight:'10px'} : {paddingLeft:'10px'}}
            >    
                {fieldLabel || 'Accept terms and conditions'}
            </a>
        </div>
        {error && <FormHelperText style={{color:'#d32f2f'}}>{fieldLabel} is required</FormHelperText>}
        </>
    )
}

// date
const DatePickerInput = ({fieldLabel, id, value, onChange, required, themeDesign }) => {

    return(
        <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
            label={`${fieldLabel}${required ? '*' : ''}`}
            value={value}
            onChange={(newValue) => {
                onChange(id,newValue,fieldLabel,required);
            }}
            fullWidth
            required={required}
            slotProps={{
                textField: {
                    fullWidth: true,
                    required: required,
                    sx: {
                        backgroundColor:[1,4]?.includes(themeDesign) ? 'transparent' : '#fff', // Background color
                        transition: 'transform 0.2s ease-out, font-size 0.2s ease-out', // Smooth transition
                        '& .MuiOutlinedInput-root': {
                            borderColor: `${themeDesign === 2 ? '#17C799' : themeDesign === 3 ? '#0A0945' : ''} !important`, // Border color
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: themeDesign === 2 ? 'rgba(0, 0, 0, 0.23)' : themeDesign === 3 ? '#0A0945' : '',  // Custom border color
                            },
                        
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: themeDesign === 2 ? '#17C799' : themeDesign === 3 ? '#0A0945' : '',  // Focused border color
                            }
                        },
                        '& .MuiInputLabel-root': {
                            color: `${themeDesign === 2 ? '#15476D' : themeDesign === 3 ? '#0A0945' : ''} !important`, // Label text color
                            opacity: themeDesign === 4 ? 1 : '0.6',
                            '&.Mui-focused': {
                                transform: 'translate(14px, -6px) scale(0.75)', // Move label up and shrink
                                fontWeight: 'bold',
                                color: `${themeDesign === 2 ? '#0A0945' : themeDesign === 3 ? '#0A0945' : ''} !important`,
                            },

                            '&.MuiInputLabel-shrink': {
                                transform: 'translate(14px, -6px) scale(0.75)', // Shrink when value is present
                            }
                        }
                    },
                }
            }}
        />
        </LocalizationProvider>
    )
};

// link
const Link = ({fieldLabel,  href }) => {
    const classes = useStyles();

    return(
        <a
            href={href}
            target="_blank"        
            rel="noopener noreferrer" 
            title={fieldLabel}
            className={classes.linkCl}
        >    
            {fieldLabel}
        </a>
    )
};

// file upload
const FileUpload = ({fieldLabel,required,value,id, onChange, error, maxFiles, maxSize, t, themeDesign}) => {
    const classes = useStyles();
    const { getRootProps, getInputProps } = useDropzone({
        onDrop: (acceptedFiles) => {
            onChange(id,acceptedFiles.map(file => Object.assign(file, {
                preview: URL.createObjectURL(file)
            })),fieldLabel,required);
        },
        ...(maxFiles ? {
            maxFiles: Number(maxFiles),
            multiple: Number(maxFiles) > 1,
        } : {}),
        ...(maxSize ? {maxSize: Number(maxSize) * 1024 * 1024} : {})
    });

    useEffect(() => {
        // Make sure to revoke the data uris to avoid memory leaks, will run on unmount
        return () => value?.forEach(file => URL.revokeObjectURL(file.preview));
    }, [value])

    const thumbs = value?.map(file => (
        <div className={classes.thumb} key={file.name}>
          <div className={classes.thumbInner}>
            <img
              src={file.preview}
              className={classes.img}
              onLoad={() => { URL.revokeObjectURL(file.preview) }}
            />
          </div>
        </div>
    ));
  
    return (
        <>
            <FormLabel component="legend" className={themeDesign === 2 ? classes.temp2MyLabelColor : themeDesign === 3 ? classes.temp3MyLabelColor : ''}>{`${fieldLabel}${required ? '*' : ''}`}</FormLabel>
            <Box 
                sx={{
                    border: `2px dashed ${themeDesign === 2 ? '#17C799' : themeDesign === 3 ? '#0A0945' : ''}`, 
                    borderRadius: '8px', 
                    padding: '20px', 
                    textAlign: 'center',
                    backgroundColor: '#f9f9f9'
                }}
                {...getRootProps()}
            >
                <input {...getInputProps()} />
                <Typography variant="h6" color={themeDesign  === 1 ? '#0BDED6' : themeDesign === 2 ? '#17C799' : themeDesign === 3 ? '#0A0945' : ''}>
                    {t && t('form.dragDrop')}
                </Typography>
                <Button 
                    variant="contained" 
                    sx={{ marginTop: '20px',backgroundColor:themeDesign === 2 ? '#17C799' : themeDesign === 3 ? '#0A0945' : '',color:'#fff' }}
                >
                    {t && t('form.browse')}
                </Button>

                {maxSize ? <FormHelperText style={{textAlign:'center',color:'#0A0945'}}>{t && t('form.maxFileSizeInfo',{size:`${maxSize} MB`})}</FormHelperText> : null}
    
                {value?.length > 0 && (
                <Box sx={{ marginTop: '20px' }}>
                    {thumbs}
                </Box>
                )}
            </Box>
            {maxFiles > 1 ? <FormHelperText style={{color:'#0A0945'}}>{t && t('form.maxFileInfo',{number: maxFiles})}</FormHelperText> : null}
            {error && <FormHelperText style={{color:'#d32f2f'}}>{fieldLabel} is required</FormHelperText>}
        </>
    );
};

const themeList = {
    1: Theme1,
    2: Theme2,
    3: Theme3,
    4: Theme4
};

const TitleList = {
    '1': T1SectionTitle,
    '2': T2SectionTitle,
    '3': T3SectionTitle,
    '4': T4SectionTitle,
};

const SubTitleList = {
    '1': T1SectionSubTitle,
    '2': T2SectionSubTitle,
    '3': T3SectionSubTitle,
    '4': T4SectionSubTitle,
};

const TextInputList = {
    '1': Temp1Input,
    '2': Temp2Input,
    '3': Temp3Input,
    '4': Temp4Input
};

const SelectInputList = {
    '1': Temp1SelctInput,
    '2': Temp2SelctInput,
    '3': Temp3SelctInput,
    '4': Temp4SelctInput
};

const fontFamilyList = {
    '1': "'Nunito Sans', sans-serif",
    '2': "'Roboto', sans-serif",
    '3': "'Poppins', sans-serif",
    '4': "'Nunito Sans', sans-serif",
};

const Form = (props) => {
    const [fields, setFields] = React.useState([]); // form fields
    const [formDetail, setFormDetail] = React.useState({}); // form detail like form name, button title, etc.
    const [formValues, setFormValues] = React.useState({}); // Store form values
    const [errors, setErrors] = React.useState({});
    // Alert message
    const [message, setMessage] = React.useState('');
    const [alertType, setAlertType] = React.useState('');
    const [alertOpen, setAlertOpen] = React.useState(false);
    const [loading, setLoading] = React.useState(true);
    const [formDetailLoading, setFormDetailLoading] = React.useState(true);

    const [formSubmitLoading,setFormSubmitLoading] = React.useState(false);

    const { data } = useCompanyData();
    const { isRTL, t, themeDesign, companyData } = data;
    const { contentData, index } = props;
    const [, , themeFontFamily] = useRTL('',fontFamilyList?.[themeDesign]);
    const companyId = companyData && companyData?.id ? companyData?.id : '';
    const matches = useMediaQuery(Theme3.breakpoints.up('lg'));
    const mobileView = useMediaQuery(Theme3.breakpoints.down('md'));
    const { buttonColor, buttonFontColor, buttonFontSize, autoButtonSize, buttonWidth, buttonHeight, sectionBackgroundColor, sectionPaddingTop, sectionPaddingBottom, sectionPaddingLeft, sectionPaddingRight,
        itemShadow, imgRadiusGeneral,buttonRadiusGeneral,
        // sectionFontColor,
        generalTitleFontSize, generalSubTitleFontSize, generalParagraphFontSize,
        generalTitleColor, generalSubTitleColor, generalParagraphColor,
        chatgptContent
    } = data || {};
    const classes = useStyles();
    const { imageShadow=true, spaceBetweenContent, textBackground, textBorder, textRadius, imageAlt, imageShadowContent, imageRadius, imageBorder, direction, buttonBorder, formId } = contentData || {};
    const _buttonColor = contentData?.buttonColor ? contentData?.buttonColor : buttonColor ? buttonColor : 'inherit';
    const _buttonFontColor = contentData?.buttonFontColor ? contentData?.buttonFontColor : buttonFontColor ? buttonFontColor : 'inherit';
    const _buttonFontSize = contentData?.buttonFontSize ? contentData?.buttonFontSize : buttonFontSize ? buttonFontSize : '16';
    const _buttonWidth = contentData?.autoButtonSize || autoButtonSize ? 'fit-content' : contentData?.buttonWidth ? `${contentData?.buttonWidth}px` :
    buttonWidth ? `${buttonWidth}px` : 'fit-content';
    const _buttonHeight = contentData?.autoButtonSize || autoButtonSize ? 'unset' : contentData?.buttonHeight ? `${contentData?.buttonHeight}px` :
    buttonHeight ? `${buttonHeight}px` : 'unset';


    const chatgptTitle = chatgptContent ? (isRTL === 'ar' ? chatgptContent?.['arabic']?.[`section${index}`]?.['title'] : chatgptContent?.['english']?.[`section${index}`]?.['title']) : '';
    const chatgptSubTitle = chatgptContent ? (isRTL === 'ar' ? chatgptContent?.['arabic']?.[`section${index}`]?.['subtitle'] : chatgptContent?.['english']?.[`section${index}`]?.['subtitle']) : '';
    const chatgptDesc = chatgptContent ? (isRTL === 'ar' ? chatgptContent?.['arabic']?.[`section${index}`]?.['description'] : chatgptContent?.['english']?.[`section${index}`]?.['description']) : '';

    const title = chatgptTitle ? chatgptTitle : isRTL === 'ar' && contentData?.arTitle ? contentData?.arTitle?.trim() : contentData?.title?.trim() || '';
    const subTitle = chatgptSubTitle ? chatgptSubTitle : isRTL === 'ar' && contentData?.arSubTitle ? contentData?.arSubTitle?.trim() : contentData?.subTitle?.trim();
    const textAlign = !mobileView ? contentData?.direction === 'left' && isRTL === 'en' ? 'left' : 
                        contentData?.direction === 'left' && isRTL === 'ar' ? 'right' :
                        contentData?.direction === 'right' && isRTL === 'en' ? 'left' :
                        contentData?.direction === 'right' && isRTL === 'ar' ? 'right' : 'center' : 'center';
    
    const pTop = contentData?.paddingTop ? contentData?.paddingTop : sectionPaddingTop ? sectionPaddingTop : null;
    const pBottom = contentData?.paddingBottom ? contentData?.paddingBottom : sectionPaddingBottom ? sectionPaddingBottom : null;

    const imgRadius = imageRadius ? `${imageRadius}px` : imgRadiusGeneral ? `${imgRadiusGeneral}px` : '0px'; 
    const btnRadius = contentData?.buttonRadius ? `${contentData?.buttonRadius}px` : buttonRadiusGeneral ? `${buttonRadiusGeneral}px` : '0px'; 

    const spaceBtwMargin = contentData?.direction === 'left' ? `0px ${spaceBetweenContent}px 0px 30px` :
    contentData?.direction === 'right' ? `0px 30px 0px ${spaceBetweenContent}px` : '0px 30px';

    const titleFontSize = contentData?.titleFontSize ? contentData?.titleFontSize : generalTitleFontSize ? generalTitleFontSize : null;
    const subTitleFontSize = contentData?.subTitleFontSize ? contentData?.subTitleFontSize : generalSubTitleFontSize ? generalSubTitleFontSize : null;
    const descriptionFontSize = contentData?.descriptionFontSize ? contentData?.descriptionFontSize : generalParagraphFontSize ? generalParagraphFontSize : null;

    const titleFontColor = contentData?.titleFontColor ? contentData?.titleFontColor : generalTitleColor || '';
    const subTitleFontColor = contentData?.subTitleFontColor ? contentData?.subTitleFontColor : generalSubTitleColor || '';
    const descFontColor = contentData?.textFontColor ? contentData?.textFontColor : generalParagraphColor || '';
    const buttonTitle = isRTL === 'ar' ? formDetail?.arButtonTitle || 'ارسال' : formDetail?.buttonTitle || 'Submit'
    const TitleComponent = TitleList?.[themeDesign];
    const SubTitleComponent = SubTitleList?.[themeDesign];
    const InputComponent = TextInputList?.[themeDesign];
    const SelectInputComponent = SelectInputList?.[themeDesign];

    const sortFields = fields?.length ? fields.sort((a, b) => a.index - b.index) : [];
    const activeTheme = themeDesign ? themeList?.[themeDesign] : null;

    const RTLTheme = useMemo(() => {
        return isRTL === 'ar' ? _RTLTheme(activeTheme) : _LTRTheme(activeTheme);
    }, [isRTL, activeTheme]);

    useEffect(()=> {
        getFields(formId);
        getFormDetail(formId);

        return () => {
            setLoading(true);
            setFormDetailLoading(true);
        };
    },[formId])

    const getFields = async (formId) => {
        if(formId) {
            try {
              const snapshot = await db
                .ref('FormFields')
                .orderByChild('formId')
                .equalTo(formId)
                .once('value');
                const data = await snapshot.val();
          
                const arr = [];
                for (const val in data) {
                    arr.push(data[val]);
                }
                setLoading(false);
                setFields(arr || [])
            }
            catch (error) {
                setLoading(false);
                console.warn('Error fetching form fields: ', error.message);
            }
        }
        else{
            setLoading(false);
        }
    };

    const getFormDetail = async (formId) => {
        if(formId) {
            try {
              const snapshot = await db
                .ref(`Forms/${formId}`)
                .once('value');
                const data = await snapshot.val();
                setFormDetail(data || {});
                setFormDetailLoading(false);
            }
            catch (error) {
                setFormDetailLoading(false);
                console.warn('Error fetching form fields: ', error.message);
            }
        }
        else{
            setFormDetailLoading(false);
        }
    };

    const handleChange = (fieldId, value, fieldLabel,required) => {
        const trimmedValue = typeof value === "string" ? value.trim() : value; // Trim only if it's a string
    
        setFormValues((prev) => ({
            ...prev,
            [`${fieldId}___${toCamelCase(fieldLabel)}`]: value
        }));
    
        // Remove error message when user starts typing
        setErrors((prev) => ({
            ...prev,
            [fieldId]: (trimmedValue === undefined || trimmedValue === null || trimmedValue === "" || trimmedValue === false) && required
        }));
    };

    const handleSubmit = async () => {
        const newErrors = {};
    
        // Validate required fields
        fields.forEach((field) => {
            const fieldKey = `${field.id}___${toCamelCase(field?.fieldLabel)}`;
            let fieldValue = formValues[fieldKey];
    
            // Apply trim if the value is a string
            if (typeof fieldValue === "string") {
                fieldValue = fieldValue.trim();
            }
    
            if (
                field.required &&
                (fieldValue === undefined || fieldValue === null || fieldValue === "" || fieldValue === false)
            ) {
                newErrors[field.id] = true;
            }
        });
    
        setErrors(newErrors);
        if(!companyId) {
            showAlert("No company id found", "error");
            return;
        }
        else if(!formDetail && !Object.keys(formDetail)?.length) {
            showAlert("No form detail found", "error");
            return;
        }
        else if (Object.keys(newErrors).length > 0) {
            showAlert("Please fill in all required fields.", "error");
            return;
        }
        else if(formDetail?.formAction === 'googleSheet' && !formDetail?.spreadSheetId) {
            showAlert("No id found to submit information", "error");
            return;
        }
        else{
            console.log("Submitting Form...", formValues);
            console.log("Form Detail", formDetail);
            setFormSubmitLoading(true);
            try {
                const updatedObj = await uploadFilesAndGetUrls(formValues,formDetail?.formAction);
                console.log("Updated Object:", updatedObj);

                processCustomFormData({
                    spreadSheetId: formDetail?.spreadSheetId,
                    formName:formDetail?.name,
                    formAction:formDetail?.formAction,
                    formData:updatedObj,
                    companyId
                })
                .then(res=> {
                    console.log('form submit response',res);
                    showAlert(res.message,'success');
                    setFormValues({});
                    setFormSubmitLoading(false);
                })
                .catch(errMsg=> {
                    setFormSubmitLoading(false);
                    showAlert(errMsg.message,'success');
                    console.log('form error',errMsg)
                })
            }
            catch (error) {
                setFormSubmitLoading(false);
                showAlert(error.message,'error');
                console.error("Error uploading files:", error);
            }
        }
    };

    const uploadFilesAndGetUrls = async (obj,formAction) => {
        const storage = firebase.storage();
        const updatedObj = { ...obj };
    
        for (const key in updatedObj) {
            if (
                Array.isArray(updatedObj[key]) && // Check if it's an array
                updatedObj[key].length > 0 && // Ensure array is not empty
                updatedObj[key].every(file => file instanceof File) // Ensure all items are File objects
            ) {
                const fileArray = updatedObj[key];
    
                // Upload each file and get the download URL
                const uploadPromises = fileArray.map(async (file) => {
                    const storageRef = storage.ref(`${companyId}/Forms/${file.name}-${Date.now()}`); // Define storage path
                    const snapshot = await storageRef.put(file); // Upload file
                    return snapshot.ref.getDownloadURL(); // Get file URL
                });
    
                const downloadUrls = await Promise.all(uploadPromises); // Replace files with URLs
                // updatedObj[key] = downloadUrls.join(", "); // Convert array to comma-separated string
                updatedObj[key] = formAction === 'googleSheet' ? downloadUrls.map(url => `("${url}")`).join("\n") : downloadUrls;
            }
        }
    
        return updatedObj;
    };

    const closeAlert = () => {
        setAlertType('');
        setAlertOpen(false);
        setMessage('');
    };

    const showAlert = (message,alertType) => {
        setMessage(message);
        setAlertType(alertType);
        setAlertOpen(true);
    };

    const LoadingSkelton = () => (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" width="60%" marginInline="auto">
            <Skeleton variant="rounded" width='100%' animation="wave" height={50}/>
            <Skeleton variant="rounded" width='100%' height={50} style={{marginTop:'15px'}}/>
            <Skeleton variant="rounded" width='100%' animation="wave" height={50} style={{marginTop:'15px'}}/>
            <Skeleton variant="rounded" width='100%' height={50} style={{marginTop:'15px'}}/>

            <Skeleton variant="rounded" width={150} animation="wave" height={60}  style={{marginTop:'20px'}} />
        </Box>
    );

    return (
        (<Box
            width="100%"
            dir="ltr"
            style={contentData?.width === 'fullWidth' ?
            {backgroundImage: contentData?.backgroundStyle === 'image' && contentData?.backgroundImage ? 
            `url(${contentData?.backgroundImage})` :
            contentData?.backgroundStyle === 'gradient' && contentData?.backgroundColor ? contentData?.backgroundColor || sectionBackgroundColor || '#ffffff' : '#ffffff',
            backgroundRepeat: contentData?.backgroundStyle === 'image' && contentData?.backgroundImage ? "no-repeat" : "",

            ...((contentData?.backgroundStyle === 'image' && contentData?.backgroundImage) ? {backgroundSize: "cover"} : {}),
            ...((contentData?.backgroundStyle === 'solidColor' && contentData?.backgroundColor) ? {backgroundColor: contentData?.backgroundColor || sectionBackgroundColor || '#ffffff'} : {backgroundColor: contentData?.backgroundColor || sectionBackgroundColor || '#ffffff'})
            }
            : {}
            }
        >
            <SectionLayout
                paddingLeft={sectionPaddingLeft}
                paddingRight={sectionPaddingRight}
                paddingTop={pTop}
                paddingBottom={pBottom}
            >
            <Box
                display="flex"
                flexDirection={'column'}
                alignItems={'center'}
                justifyContent={'center'}
                width={"100%"}
                m="auto"
                position="relative"
                height={contentData?.height ? `${contentData?.height}px` : 'auto'}
                style={contentData?.width === 'boxed' ?
                {backgroundImage: contentData?.backgroundStyle === 'image' && contentData?.backgroundImage ? 
                `url(${contentData?.backgroundImage})` :
                contentData?.backgroundStyle === 'gradient' && contentData?.backgroundColor ? contentData?.backgroundColor || sectionBackgroundColor || 'transparent' : 'transparent',
                backgroundRepeat: contentData?.backgroundStyle === 'image' && contentData?.backgroundImage ? "no-repeat" : "",
    
                ...((contentData?.backgroundStyle === 'image' && contentData?.backgroundImage) ? {backgroundSize: "cover"} : {}),
                ...((contentData?.backgroundStyle === 'solidColor' && contentData?.backgroundColor) ? {backgroundColor: contentData?.backgroundColor || sectionBackgroundColor || 'transparent'} : {backgroundColor: contentData?.backgroundColor || sectionBackgroundColor || 'transparent'})
                }
                : {}
                }
            >
                {title ?
                <Box
                    display={'flex'}
                    flexDirection="column"
                    alignItems="center"
                    width="100%"
                >
                    <TitleComponent {...(titleFontColor ? {color: titleFontColor} : {} )} pFontSize={titleFontSize} wordBreak="break-word" textAlign={'center'} {...([1,4]?.includes(themeDesign) ? {contentSection: true } : {} )}>{title}</TitleComponent>
                </Box>
                : null}

                {subTitle ?
                <Box width="100%">
                    <SubTitleComponent {...(subTitleFontColor ? {color: subTitleFontColor} : {} )} pFontSize={subTitleFontSize} wordBreak="break-word" visibleHorizontalLine={false} textAlign={'center'} justifyContent="center">{subTitle}</SubTitleComponent>
                </Box>
                : null}

                <Box
                    mb={matches ? 0 : 4}
                    p="5px"
                    minWidth="90%"
                >
                    {loading || formDetailLoading && !themeDesign ?
                        <LoadingSkelton />
                    :
                        <Box width="60%" style={{marginInline: 'auto',position:'relative',...(formSubmitLoading ? {padding:'10px'} : {})}}>
                            {sortFields?.length ?
                            <>
                            {sortFields?.map(field => {
                                return(
                                    <>
                                    {isRTL === 'ar' ? (
                                        <CacheProvider value={cacheRtl}>
                                        <ThemeProvider theme={RTLTheme}>
                                            <Box pb={field?.fieldType !== 'lineBreak' ? 2 : 0} key={field.id} position="relative"dir={isRTL === 'ar' ? 'rtl' : 'ltr'}>
                                                {field?.fieldType === 'dropdown' ?
                                                    <SelectInputComponent 
                                                        {...field}
                                                        value={formValues[`${field.id}___${toCamelCase(field?.fieldLabel)}`] || ""}
                                                        onChange={handleChange}
                                                        error={errors[field.id] || false}
                                                        t={t}
                                                        isRTL={isRTL}
                                                        themeDesign={themeDesign}
                                                    />
                                                :
                                                field?.fieldType === 'phone' ?
                                                    <TempPhoneInput 
                                                        {...field}
                                                        value={formValues[`${field.id}___${toCamelCase(field?.fieldLabel)}`] || ""}
                                                        onChange={handleChange}
                                                        error={errors[field.id] || false}
                                                        t={t}
                                                        isRTL={isRTL}
                                                        themeDesign={themeDesign}
                                                    />
                                                :
                                                field?.fieldType === 'checkbox' ?
                                                    <CheckboxInput 
                                                        {...field}
                                                        value={formValues[`${field.id}___${toCamelCase(field?.fieldLabel)}`] || ""}
                                                        onChange={handleChange}
                                                        error={errors[field.id] || false}
                                                        t={t}
                                                        isRTL={isRTL}
                                                        themeDesign={themeDesign}
                                                    />
                                                :
                                                field?.fieldType === 'terms' ?
                                                    <TermsCheckbox 
                                                        {...field}
                                                        value={formValues[`${field.id}___${toCamelCase(field?.fieldLabel)}`] || ""}
                                                        onChange={handleChange}
                                                        error={errors[field.id] || false}
                                                        t={t}
                                                        isRTL={isRTL}
                                                        themeDesign={themeDesign}
                                                    />
                                                :
                                                field?.fieldType === 'date' ?
                                                    <DatePickerInput 
                                                        {...field}
                                                        value={formValues[`${field.id}___${toCamelCase(field?.fieldLabel)}`] || null}
                                                        onChange={handleChange}
                                                        error={errors[field.id] || false}
                                                        themeDesign={themeDesign}
                                                        t={t}
                                                        isRTL={isRTL}
                                                    />
                                                :
                                                field?.fieldType === 'website' ?
                                                    <Link
                                                        {...field}
                                                        t={t}
                                                        themeDesign={themeDesign}
                                                        isRTL={isRTL}
                                                    />
                                                :
                                                field?.fieldType === 'file' ?
                                                    <FileUpload
                                                        {...field}
                                                        value={formValues[`${field.id}___${toCamelCase(field?.fieldLabel)}`] || []}
                                                        onChange={handleChange}
                                                        error={errors[field.id] || false}
                                                        themeDesign={themeDesign}
                                                        isRTL={isRTL}
                                                        t={t}
                                                    />
                                                :
                                                field?.fieldType === 'lineBreak' ?
                                                    <Divider style={{borderWidth:'1px',margin:'20px 0px'}}/>
                                                :
                                                    <InputComponent 
                                                        {...field}
                                                        value={formValues[`${field.id}___${toCamelCase(field?.fieldLabel)}`] || ""}
                                                        onChange={handleChange}
                                                        error={errors[field.id] || false}
                                                        themeDesign={themeDesign}
                                                        isRTL={isRTL}
                                                    />
                                                }
                                            </Box>
                                        </ThemeProvider>
                                        </CacheProvider>
                                        )
                                        :
                                        (
                                            <Box pb={field?.fieldType !== 'lineBreak' ? 2 : 0} key={field.id} position="relative">
                                                {field?.fieldType === 'dropdown' ?
                                                    <SelectInputComponent 
                                                        {...field}
                                                        value={formValues[`${field.id}___${toCamelCase(field?.fieldLabel)}`] || ""}
                                                        onChange={handleChange}
                                                        dir={isRTL === 'ar' ? 'rtl' : 'ltr'}
                                                        error={errors[field.id] || false}
                                                        t={t}
                                                    />
                                                :
                                                field?.fieldType === 'phone' ?
                                                    <TempPhoneInput 
                                                        {...field}
                                                        value={formValues[`${field.id}___${toCamelCase(field?.fieldLabel)}`] || ""}
                                                        onChange={handleChange}
                                                        error={errors[field.id] || false}
                                                        themeDesign={themeDesign}
                                                        t={t}
                                                        dir={isRTL === 'ar' ? 'rtl' : 'ltr'}
                                                    />
                                                :
                                                field?.fieldType === 'checkbox' ?
                                                    <CheckboxInput 
                                                        {...field}
                                                        value={formValues[`${field.id}___${toCamelCase(field?.fieldLabel)}`] || ""}
                                                        onChange={handleChange}
                                                        error={errors[field.id] || false}
                                                        themeDesign={themeDesign}
                                                        t={t}
                                                        dir={isRTL === 'ar' ? 'rtl' : 'ltr'}
                                                    />
                                                :
                                                field?.fieldType === 'terms' ?
                                                    <TermsCheckbox 
                                                        {...field}
                                                        value={formValues[`${field.id}___${toCamelCase(field?.fieldLabel)}`] || ""}
                                                        onChange={handleChange}
                                                        error={errors[field.id] || false}
                                                        t={t}
                                                        isRTL={isRTL}
                                                        themeDesign={themeDesign}
                                                    />
                                                :
                                                field?.fieldType === 'date' ?
                                                    <DatePickerInput 
                                                        {...field}
                                                        value={formValues[`${field.id}___${toCamelCase(field?.fieldLabel)}`] || null}
                                                        onChange={handleChange}
                                                        error={errors[field.id] || false}
                                                        themeDesign={themeDesign}
                                                        t={t}
                                                        dir={isRTL === 'ar' ? 'rtl' : 'ltr'}
                                                    />
                                                :
                                                field?.fieldType === 'website' ?
                                                    <Link
                                                        {...field}
                                                        t={t}
                                                        themeDesign={themeDesign}
                                                        dir={isRTL === 'ar' ? 'rtl' : 'ltr'}
                                                    />
                                                :
                                                field?.fieldType === 'file' ?
                                                    <FileUpload
                                                        {...field}
                                                        value={formValues[`${field.id}___${toCamelCase(field?.fieldLabel)}`] || []}
                                                        onChange={handleChange}
                                                        error={errors[field.id] || false}
                                                        themeDesign={themeDesign}
                                                        dir={isRTL === 'ar' ? 'rtl' : 'ltr'}
                                                        t={t}
                                                    />
                                                :
                                                field?.fieldType === 'lineBreak' ?
                                                    <Divider style={{borderWidth:'1px',margin:'20px 0px'}}/>
                                                :
                                                    <InputComponent 
                                                        {...field}
                                                        value={formValues[`${field.id}___${toCamelCase(field?.fieldLabel)}`] || ""}
                                                        onChange={handleChange}
                                                        error={errors[field.id] || false}
                                                        themeDesign={themeDesign}
                                                        isRTL={isRTL}
                                                        dir={isRTL === 'ar' ? 'rtl' : 'ltr'}
                                                    />
                                                }
                                            </Box>
                                        )
                                    }
                                    </>
                                )          
                            })}
                                <Box textAlign={'center'}>
                                    <CustomButton
                                    style={{
                                        backgroundColor: _buttonColor,
                                        textTransform:'capitalize',
                                        color: _buttonFontColor,
                                        width: _buttonWidth,
                                        height: _buttonHeight,
                                        borderRadius: btnRadius,
                                        fontFamily: 'inherit',
                                        ...(buttonBorder ? {border:buttonBorder} : {})
                                    }}
                                    // onClick={!formSubmitLoading ? handleSubmit : null}
                                    className={textClasses?.[_buttonFontSize]}
                                    >
                                    {buttonTitle}
                                    </CustomButton>
                                </Box>
                            </>
                            : null
                            }

                            <Backdrop className={classes.dialogBackdrop} open={formSubmitLoading}>
                                <ColorCircularProgress />
                            </Backdrop>
                        </Box>
                    }
                </Box>
            </Box>
            </SectionLayout>

            <AlertMessage {...{alertType,message,snackOpen:alertOpen,closeAlert}} />

        </Box>)
    );
};

const CustomButton = ({ children, ...props }) => {
    return (
      <Button size="large" variant="contained" color="primary" {...props}>
        {children}
      </Button>
    );
};

export default Form;

const toCamelCase = (str) => {
    return str
      .split(" ")
      .map((word, index) =>
        index === 0 ? word.toLowerCase() : word.charAt(0).toUpperCase() + word.slice(1)
      )
      .join("");
};